/**
 * CurrencyAmount Component
 * A composite field with currency dropdown and numeric input with formatting
 * 
 * @description Provides a styled composite input with:
 * - Currency dropdown (from Currencies standing data)
 * - Numeric input with 2 decimal places and thousands separators
 * - Parsing of formatted strings like "£1,234.56"
 * - Size variants (xs, sm, md, lg)
 * - Color variants (primary, secondary, accent, etc.)
 * 
 * @example
 * ```typescript
 * const currencyAmount = new CurrencyAmount(container, {
 *   name: 'salary',
 *   label: 'Salary',
 *   currencies: [
 *     { CurrencyId: 1, Symbol: '£', Description: 'Pound Sterling' },
 *     { CurrencyId: 2, Symbol: '$', Description: 'US Dollars' }
 *   ],
 *   currencyId: 1,
 *   amount: 50000,
 *   size: 'sm'
 * });
 * await currencyAmount.initialize();
 * ```
 */

import { IComponent } from './IComponent';
import { Logger } from '@/services/Logger';

const log = Logger.scope('CurrencyAmount');

/**
 * Currency data structure from cached standing data
 */
export interface Currency {
  CurrencyId: number;
  Symbol: string;
  Description: string;
  Ordering?: number;
}

/**
 * Value returned by CurrencyAmount component
 */
export interface CurrencyAmountValue {
  /** The currency ID */
  currencyId: number;
  /** The currency symbol */
  symbol: string;
  /** The numeric amount */
  amount: number;
  /** Formatted display string e.g. "£1,234.56" */
  formatted: string;
}

/**
 * Configuration options for CurrencyAmount component
 */
export interface CurrencyAmountOptions {
  /** Unique name for the field */
  name: string;
  /** Optional label displayed above the field */
  label?: string;
  /** Array of currencies from standing data */
  currencies: Currency[];
  /** Currently selected currency ID */
  currencyId?: number;
  /** Initial amount value */
  amount?: number;
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Color variant */
  color?: 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error';
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is readonly */
  readonly?: boolean;
  /** Placeholder for amount input */
  placeholder?: string;
  /** Number of decimal places (default 2) */
  decimals?: number;
  /** Minimum amount */
  min?: number;
  /** Maximum amount */
  max?: number;
  /** CSS classes to add to the container */
  containerClass?: string;
  /** Callback when value changes */
  onChange?: (value: CurrencyAmountValue) => void;
}

/**
 * CurrencyAmount Component Class
 * Implements a composite currency + amount input
 */
export class CurrencyAmount implements IComponent {
  private container: HTMLElement;
  private options: CurrencyAmountOptions;
  private selectElement: HTMLSelectElement | null = null;
  private inputElement: HTMLInputElement | null = null;
  private initialized = false;

  constructor(container: HTMLElement, options: CurrencyAmountOptions) {
    this.container = container;
    this.options = {
      size: 'sm',
      decimals: 2,
      currencyId: options.currencies[0]?.CurrencyId,
      amount: 0,
      ...options
    };
  }

  /**
   * Initialize the component
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    this.render();
    this.bindEvents();
    this.initialized = true;

    log.debug('CurrencyAmount initialized', {
      name: this.options.name,
      currencyId: this.options.currencyId,
      amount: this.options.amount
    });
  }

  /**
   * Render the component
   */
  private render(): void {
    const {
      name,
      label,
      currencies,
      currencyId,
      amount,
      size,
      color,
      disabled,
      required,
      readonly: isReadonly,
      placeholder,
      containerClass
    } = this.options;

    // Build size class
    const sizeClass = size ? `select-${size}` : 'select-sm';
    const inputSizeClass = size ? `input-${size}` : 'input-sm';
    
    // Build color class
    const colorClass = color ? `select-${color}` : '';
    const inputColorClass = color ? `input-${color}` : '';

    // Build currency options HTML
    const currencyOptionsHtml = currencies
      .sort((a, b) => (a.Ordering ?? 999) - (b.Ordering ?? 999))
      .map(c => `<option value="${c.CurrencyId}" ${c.CurrencyId === currencyId ? 'selected' : ''}>${c.Symbol}</option>`)
      .join('');

    // Format initial amount
    const formattedAmount = this.formatNumber(amount ?? 0);

    // Build the HTML
    const html = `
      <div class="currency-amount-container ${containerClass ?? ''}">
        ${label ? `<label class="label"><span class="label-text">${label}${required ? ' <span class="text-error">*</span>' : ''}</span></label>` : ''}
        <div class="join w-full">
          <select 
            name="${name}_currency" 
            class="select select-bordered ${sizeClass} ${colorClass} join-item w-20 min-w-20"
            ${disabled ? 'disabled' : ''}
            ${isReadonly ? 'disabled' : ''}
          >
            ${currencyOptionsHtml}
          </select>
          <input 
            type="text" 
            name="${name}_amount"
            class="input input-bordered ${inputSizeClass} ${inputColorClass} join-item flex-1 text-right font-mono"
            value="${formattedAmount}"
            placeholder="${placeholder ?? '0.00'}"
            inputmode="decimal"
            ${disabled ? 'disabled' : ''}
            ${isReadonly ? 'readonly' : ''}
          />
        </div>
      </div>
    `;

    this.container.innerHTML = html;

    // Get references
    this.selectElement = this.container.querySelector(`select[name="${name}_currency"]`);
    this.inputElement = this.container.querySelector(`input[name="${name}_amount"]`);
  }

  /**
   * Bind event listeners
   */
  private bindEvents(): void {
    // Currency change
    this.selectElement?.addEventListener('change', () => {
      this.notifyChange();
    });

    // Amount input - format on blur
    this.inputElement?.addEventListener('blur', () => {
      const rawValue = this.parseNumber(this.inputElement!.value);
      this.inputElement!.value = this.formatNumber(rawValue);
      this.notifyChange();
    });

    // Allow only numeric input
    this.inputElement?.addEventListener('keydown', (e) => {
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
      
      if (allowedKeys.includes(e.key)) return;
      
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
      
      // Allow numbers, period, comma, minus
      if (/[0-9.,\-]/.test(e.key)) return;
      
      e.preventDefault();
    });

    // Format on paste
    this.inputElement?.addEventListener('paste', (e) => {
      e.preventDefault();
      const pastedText = e.clipboardData?.getData('text') ?? '';
      const numericValue = this.parseNumber(pastedText);
      this.inputElement!.value = this.formatNumber(numericValue);
      this.notifyChange();
    });
  }

  /**
   * Format a number with thousands separators and decimals
   */
  private formatNumber(value: number): string {
    const decimals = this.options.decimals ?? 2;
    return value.toLocaleString('en-GB', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  /**
   * Parse a formatted number string to a number
   */
  private parseNumber(text: string): number {
    if (!text || text.trim() === '') return 0;
    
    // Remove currency symbols and whitespace
    let cleaned = text.replace(/[£$€¥₹₩₪]/g, '').trim();
    
    // Remove thousands separators (commas)
    cleaned = cleaned.replace(/,/g, '');
    
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Parse a formatted string like "£1,234.56" into components
   */
  static parse(text: string, currencies: Currency[]): CurrencyAmountValue | null {
    if (!text || text.trim() === '') return null;

    // Try to extract currency symbol from the beginning
    let symbol = '';
    let amountStr = text.trim();
    
    for (const currency of currencies) {
      if (amountStr.startsWith(currency.Symbol)) {
        symbol = currency.Symbol;
        amountStr = amountStr.substring(currency.Symbol.length).trim();
        break;
      }
    }

    // Parse the amount
    const cleaned = amountStr.replace(/,/g, '');
    const amount = parseFloat(cleaned);
    
    if (isNaN(amount)) return null;

    // Find the currency
    const currency = currencies.find(c => c.Symbol === symbol) ?? currencies[0];
    
    return {
      currencyId: currency?.CurrencyId ?? 0,
      symbol: currency?.Symbol ?? '',
      amount,
      formatted: `${currency?.Symbol ?? ''}${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    };
  }

  /**
   * Notify onChange callback
   */
  private notifyChange(): void {
    if (this.options.onChange) {
      this.options.onChange(this.getValue());
    }
  }

  /**
   * Get the current value
   */
  getValue(): CurrencyAmountValue {
    const currencyId = parseInt(this.selectElement?.value ?? '0', 10);
    const currency = this.options.currencies.find(c => c.CurrencyId === currencyId);
    const amount = this.parseNumber(this.inputElement?.value ?? '0');
    const symbol = currency?.Symbol ?? '';

    return {
      currencyId,
      symbol,
      amount,
      formatted: `${symbol}${this.formatNumber(amount)}`
    };
  }

  /**
   * Set the value
   */
  setValue(currencyId: number, amount: number): void {
    if (this.selectElement) {
      this.selectElement.value = currencyId.toString();
    }
    if (this.inputElement) {
      this.inputElement.value = this.formatNumber(amount);
    }
  }

  /**
   * Set value from a formatted string like "£1,234.56"
   */
  setValueFromString(text: string): void {
    const parsed = CurrencyAmount.parse(text, this.options.currencies);
    if (parsed) {
      this.setValue(parsed.currencyId, parsed.amount);
    }
  }

  /**
   * Get the amount only
   */
  getAmount(): number {
    return this.parseNumber(this.inputElement?.value ?? '0');
  }

  /**
   * Set the amount only
   */
  setAmount(amount: number): void {
    if (this.inputElement) {
      this.inputElement.value = this.formatNumber(amount);
    }
  }

  /**
   * Get the selected currency
   */
  getCurrency(): Currency | undefined {
    const currencyId = parseInt(this.selectElement?.value ?? '0', 10);
    return this.options.currencies.find(c => c.CurrencyId === currencyId);
  }

  /**
   * Set the currency by ID
   */
  setCurrency(currencyId: number): void {
    if (this.selectElement) {
      this.selectElement.value = currencyId.toString();
    }
  }

  /**
   * Enable the component
   */
  enable(): void {
    if (this.selectElement) this.selectElement.disabled = false;
    if (this.inputElement) this.inputElement.disabled = false;
  }

  /**
   * Disable the component
   */
  disable(): void {
    if (this.selectElement) this.selectElement.disabled = true;
    if (this.inputElement) this.inputElement.disabled = true;
  }

  /**
   * Check if initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Destroy the component
   */
  destroy(): void {
    this.container.innerHTML = '';
    this.selectElement = null;
    this.inputElement = null;
    this.initialized = false;
  }
}
